import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Link2, Film, Search, Upload, ListPlus, Pencil, Star, IndianRupee, GripVertical, ArrowUp, ArrowDown, Settings, KeyRound, Mail, Check, X, Tags, Type } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function parseBulk(text, defaultCat) {
  const items = [];
  text.split(/\r?\n/).forEach((line) => {
    const l = line.trim();
    if (!l) return;
    if (/^(link|url)/i.test(l) && /title/i.test(l)) return; // skip header row
    let parts = l.includes("|") ? l.split("|") : l.split(",");
    parts = parts.map((p) => p.trim());
    const url = parts[0];
    if (!url) return;
    items.push({ url, title: parts[1] || "", category: parts[2] || defaultCat });
  });
  return items;
}

export default function AdminDashboard() {
  const { user, changePassword, changeEmail } = useAuth();
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [whatsapp, setWhatsapp] = useState("919630024060");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkCat, setBulkCat] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [priceInput, setPriceInput] = useState("");
  const [currency, setCurrency] = useState("₹");
  const [savingPrice, setSavingPrice] = useState(false);
  const [edit, setEdit] = useState(null); // {id,title,category,url,originalUrl,featured,price}
  const [editSaving, setEditSaving] = useState(false);
  const [addPrice, setAddPrice] = useState("");
  const [feats, setFeats] = useState([]);
  const dragIndex = useRef(null);
  const [acctOpen, setAcctOpen] = useState(false);
  const [cpCur, setCpCur] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpBusy, setCpBusy] = useState(false);
  const [emPwd, setEmPwd] = useState("");
  const [emNew, setEmNew] = useState("");
  const [emBusy, setEmBusy] = useState(false);
  const [inlineId, setInlineId] = useState(null);
  const [inlineTitle, setInlineTitle] = useState("");
  const [inlineBusy, setInlineBusy] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [catBusy, setCatBusy] = useState(false);

  const loadVideos = () =>
    api.get("/videos", { params: { limit: 500 } }).then(({ data }) => setVideos(data));

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      setCategories(data.categories);
      setWhatsapp(data.whatsapp_number);
      setPriceInput(String(data.price ?? ""));
      setCurrency(data.currency || "₹");
    });
    loadVideos();
  }, []);

  useEffect(() => {
    setFeats(
      videos.filter((v) => v.featured).sort((a, b) => (a.featured_order ?? 0) - (b.featured_order ?? 0))
    );
  }, [videos]);

  const addVideo = async (e) => {
    e.preventDefault();
    if (!title.trim() || !category || !url.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSaving(true);
    try {
      const body = { title, category, url };
      if (addPrice.trim() !== "") body.price = Number(addPrice);
      await api.post("/videos", body);
      toast.success("Invite added to the catalogue!");
      setTitle(""); setCategory(""); setUrl(""); setAddPrice("");
      loadVideos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add video.");
    } finally {
      setSaving(false);
    }
  };

  const deleteVideo = async (id) => {
    try {
      await api.delete(`/videos/${id}`);
      toast.success("Invite removed.");
      loadVideos();
    } catch (err) {
      toast.error("Failed to delete video.");
    }
  };

  const runBulkImport = async () => {
    const items = parseBulk(bulkText, bulkCat);
    if (!items.length) {
      toast.error("Add at least one Vimeo link.");
      return;
    }
    if (items.some((it) => !it.category)) {
      toast.error("Pick a default category, or add a category to each line.");
      return;
    }
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const { data } = await api.post("/videos/bulk", { items });
      setBulkResult(data);
      toast.success(
        `${data.added} invite(s) imported${data.failed.length ? `, ${data.failed.length} skipped` : ""}.`
      );
      if (data.added) setBulkText("");
      loadVideos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bulk import failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const onCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(String(reader.result || ""));
    reader.readAsText(file);
    e.target.value = "";
  };

  const savePrice = async () => {
    const p = parseFloat(priceInput);
    if (isNaN(p) || p < 0) {
      toast.error("Enter a valid price.");
      return;
    }
    setSavingPrice(true);
    try {
      await api.put("/settings", { price: p });
      toast.success(`Price updated to ${currency}${p}.`);
    } catch (err) {
      toast.error("Failed to update price.");
    } finally {
      setSavingPrice(false);
    }
  };

  const toggleFeatured = async (v) => {
    try {
      await api.put(`/videos/${v.id}`, { featured: !v.featured });
      toast.success(!v.featured ? "Added to Featured." : "Removed from Featured.");
      loadVideos();
    } catch (err) {
      toast.error("Failed to update featured status.");
    }
  };

  const openEdit = (v) =>
    setEdit({
      id: v.id, title: v.title, category: v.category, url: v.source_url,
      originalUrl: v.source_url, featured: v.featured,
      price: v.price != null ? String(v.price) : "",
    });

  const saveEdit = async () => {
    if (!edit.title.trim() || !edit.category) {
      toast.error("Title and category are required.");
      return;
    }
    if (edit.price.trim() !== "" && isNaN(Number(edit.price))) {
      toast.error("Enter a valid price, or leave blank for the default.");
      return;
    }
    const body = { title: edit.title, category: edit.category };
    if (edit.url && edit.url !== edit.originalUrl) body.url = edit.url;
    body.price = edit.price.trim() === "" ? null : Number(edit.price);
    setEditSaving(true);
    try {
      await api.put(`/videos/${edit.id}`, body);
      toast.success("Invite updated.");
      setEdit(null);
      loadVideos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update.");
    } finally {
      setEditSaving(false);
    }
  };

  const persistOrder = (arr) => {
    api.put("/videos/featured/order", { ids: arr.map((v) => v.id) })
      .then(() => toast.success("Featured order saved."))
      .catch(() => { toast.error("Failed to save order."); loadVideos(); });
  };

  const moveFeat = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= feats.length) return;
    const arr = [...feats];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setFeats(arr);
    persistOrder(arr);
  };

  const onFeatDrop = (i) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from == null || from === i) return;
    const arr = [...feats];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    setFeats(arr);
    persistOrder(arr);
  };

  const submitPassword = async () => {
    if (!cpCur || cpNew.length < 6) {
      toast.error("Enter your current password and a new one (min 6 characters).");
      return;
    }
    setCpBusy(true);
    try {
      await changePassword(cpCur, cpNew);
      toast.success("Password changed successfully.");
      setCpCur(""); setCpNew("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change password.");
    } finally {
      setCpBusy(false);
    }
  };

  const submitEmail = async () => {
    if (!emPwd || !emNew.trim()) {
      toast.error("Enter your current password and the new email.");
      return;
    }
    setEmBusy(true);
    try {
      await changeEmail(emPwd, emNew.trim());
      toast.success("Email updated. Use it next time you log in.");
      setEmPwd(""); setEmNew("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change email.");
    } finally {
      setEmBusy(false);
    }
  };

  const startInline = (v) => { setInlineId(v.id); setInlineTitle(v.title); };
  const cancelInline = () => { setInlineId(null); setInlineTitle(""); };
  const saveInline = async () => {
    if (!inlineTitle.trim()) {
      toast.error("Title cannot be empty.");
      return;
    }
    setInlineBusy(true);
    try {
      await api.put(`/videos/${inlineId}`, { title: inlineTitle.trim() });
      toast.success("Title updated.");
      setInlineId(null); setInlineTitle("");
      loadVideos();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to rename.");
    } finally {
      setInlineBusy(false);
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) {
      toast.error("Enter a category name.");
      return;
    }
    setCatBusy(true);
    try {
      const { data } = await api.post("/categories", { name: newCat.trim() });
      setCategories(data.categories);
      toast.success(`Category "${newCat.trim()}" added.`);
      setNewCat("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add category.");
    } finally {
      setCatBusy(false);
    }
  };

  const removeCategory = async (name) => {
    try {
      const { data } = await api.delete(`/categories/${encodeURIComponent(name)}`);
      setCategories(data.categories);
      toast.success(`Category "${name}" removed.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to remove category.");
    }
  };

  const filtered = videos.filter((v) =>
    v.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="App min-h-screen">
      <Header whatsappNumber={whatsapp} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <span className="text-xs tracking-widest uppercase font-semibold text-amber-700">Admin Dashboard</span>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-bold text-amber-950">Manage Invitations</h1>
            <p className="mt-2 text-stone-600">Add, edit and curate your public catalogue.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="account-settings-button"
                onClick={() => setAcctOpen(true)}
                variant="outline"
                className="border-amber-500/40 text-amber-900 hover:bg-amber-50"
              >
                <Settings className="h-4 w-4 mr-1.5" /> Account Settings
              </Button>
              <Button
                data-testid="manage-categories-button"
                onClick={() => setCatOpen(true)}
                variant="outline"
                className="border-amber-500/40 text-amber-900 hover:bg-amber-50"
              >
                <Tags className="h-4 w-4 mr-1.5" /> Manage Categories
              </Button>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-amber-500/25 p-4 shadow-sm">
            <Label className="text-amber-900 font-medium text-sm flex items-center gap-1.5">
              <IndianRupee className="h-4 w-4 text-amber-600" /> Price for all invites
            </Label>
            <div className="mt-2 flex items-center gap-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-700 font-medium">{currency}</span>
                <Input
                  data-testid="price-input"
                  type="number"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  className="h-11 w-32 pl-7 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40 font-semibold"
                />
              </div>
              <Button
                data-testid="save-price-button"
                onClick={savePrice}
                disabled={savingPrice}
                className="h-11 bg-amber-950 hover:bg-amber-900 text-amber-50"
              >
                {savingPrice ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Add form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-5 xl:col-span-4"
          >
            <div className="sticky top-28 bg-white rounded-2xl border border-amber-500/25 shadow-[0_4px_20px_-4px_rgba(212,175,55,0.15)] p-6 sm:p-7">
              <Tabs defaultValue="single">
                <TabsList data-testid="admin-tabs" className="grid grid-cols-2 w-full mb-6 bg-amber-100/60">
                  <TabsTrigger value="single" data-testid="tab-single" className="data-[state=active]:bg-amber-950 data-[state=active]:text-amber-50">
                    <Plus className="h-4 w-4 mr-1.5" /> Add One
                  </TabsTrigger>
                  <TabsTrigger value="bulk" data-testid="tab-bulk" className="data-[state=active]:bg-amber-950 data-[state=active]:text-amber-50">
                    <ListPlus className="h-4 w-4 mr-1.5" /> Bulk Import
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="single">
                  <form data-testid="add-video-form" onSubmit={addVideo} className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-amber-900 font-medium">Vimeo Link</Label>
                      <div className="relative">
                        <Link2 className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600" />
                        <Input
                          data-testid="video-url-input"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                          placeholder="Paste a Vimeo link (e.g. https://vimeo.com/123...)"
                          className="h-11 pl-10 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-amber-900 font-medium">Invite Title</Label>
                      <Input
                        data-testid="video-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Royal Palace Wedding Invitation"
                        className="h-11 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-amber-900 font-medium">Event Category</Label>
                      <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger
                          data-testid="video-category-select"
                          className="h-11 bg-[#FAF6F0] border-amber-500/30 focus:ring-amber-500/40"
                        >
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-amber-900 font-medium">Price <span className="font-normal text-stone-400">(optional — blank uses default {currency}{priceInput})</span></Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-700 font-medium">{currency}</span>
                        <Input
                          data-testid="video-price-input"
                          type="number"
                          value={addPrice}
                          onChange={(e) => setAddPrice(e.target.value)}
                          placeholder={`${priceInput} (default)`}
                          className="h-11 pl-7 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
                        />
                      </div>
                    </div>

                    <Button
                      data-testid="submit-video-button"
                      type="submit"
                      disabled={saving}
                      className="w-full h-12 bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:shadow-amber-500/25 text-white font-semibold shimmer-btn"
                    >
                      {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><Plus className="h-5 w-5 mr-1.5" /> Add to Catalogue</>)}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="bulk">
                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-amber-900 font-medium">Default Category</Label>
                      <Select value={bulkCat} onValueChange={setBulkCat}>
                        <SelectTrigger data-testid="bulk-category-select" className="h-11 bg-[#FAF6F0] border-amber-500/30">
                          <SelectValue placeholder="Used when a line has no category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c} value={c} data-testid={`bulk-category-option-${c}`}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-amber-900 font-medium">Paste invites</Label>
                        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 cursor-pointer">
                          <Upload className="h-3.5 w-3.5" /> Upload CSV
                          <input data-testid="bulk-csv-input" type="file" accept=".csv,text/csv,text/plain" onChange={onCsvUpload} className="hidden" />
                        </label>
                      </div>
                      <Textarea
                        data-testid="bulk-textarea"
                        value={bulkText}
                        onChange={(e) => setBulkText(e.target.value)}
                        rows={8}
                        placeholder={"One invite per line:\nhttps://vimeo.com/123 | Royal Wedding | Weddings\nhttps://vimeo.com/456 | Sangeet Night | Weddings"}
                        className="bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40 font-mono text-xs"
                      />
                      <p className="text-xs text-stone-500">
                        Format: <span className="font-mono">link | title | category</span>. Title & category are optional (default category is used). CSV columns: Link, Title, Category.
                      </p>
                    </div>

                    <Button
                      data-testid="bulk-import-button"
                      onClick={runBulkImport}
                      disabled={bulkBusy}
                      className="w-full h-12 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-semibold shimmer-btn"
                    >
                      {bulkBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><ListPlus className="h-5 w-5 mr-1.5" /> Import All</>)}
                    </Button>

                    {bulkResult && (
                      <div data-testid="bulk-result" className="text-sm rounded-lg border border-amber-300/60 bg-amber-50 p-3">
                        <p className="font-medium text-emerald-700">{bulkResult.added} imported</p>
                        {bulkResult.failed?.length > 0 && (
                          <div className="mt-1 text-red-700">
                            <p className="font-medium">{bulkResult.failed.length} skipped:</p>
                            <ul className="mt-1 list-disc list-inside text-xs text-red-600 max-h-32 overflow-auto">
                              {bulkResult.failed.slice(0, 20).map((f, i) => (
                                <li key={i}>{f.item} — {f.reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </motion.div>

          {/* List */}
          <div className="lg:col-span-7 xl:col-span-8">
            {feats.length > 0 && (
              <div data-testid="featured-order-manager" className="mb-6 bg-white rounded-2xl border border-amber-500/25 p-5 shadow-[0_4px_20px_-4px_rgba(212,175,55,0.15)]">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="h-5 w-5 text-amber-500 fill-current" />
                  <h3 className="font-serif text-lg font-semibold text-amber-950">Featured Order</h3>
                </div>
                <p className="text-xs text-stone-500 mb-4">Drag rows (or use arrows) to set the spotlight order. Saves automatically.</p>
                <div className="space-y-2">
                  {feats.map((v, i) => (
                    <div
                      key={v.id}
                      data-testid={`featured-order-row-${v.id}`}
                      draggable
                      onDragStart={() => { dragIndex.current = i; }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onFeatDrop(i)}
                      className="flex items-center gap-3 bg-amber-50/60 border border-amber-300/50 rounded-xl p-2.5 cursor-grab active:cursor-grabbing"
                    >
                      <GripVertical className="h-4 w-4 text-amber-500 shrink-0" />
                      <span className="grid place-items-center h-6 w-6 rounded-full bg-amber-950 text-amber-50 text-xs font-bold shrink-0">{i + 1}</span>
                      <img src={v.thumbnail} alt="" className="h-9 w-14 object-cover rounded shrink-0 bg-stone-200" />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-amber-950">{v.title}</span>
                      <button
                        data-testid={`featured-up-${v.id}`}
                        onClick={() => moveFeat(i, -1)}
                        disabled={i === 0}
                        className="p-1 text-amber-700 hover:text-amber-900 disabled:opacity-30"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </button>
                      <button
                        data-testid={`featured-down-${v.id}`}
                        onClick={() => moveFeat(i, 1)}
                        disabled={i === feats.length - 1}
                        className="p-1 text-amber-700 hover:text-amber-900 disabled:opacity-30"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
              <h2 className="font-serif text-xl font-semibold text-amber-950">
                Catalogue <span className="text-amber-600">({videos.length})</span>
              </h2>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600" />
                <Input
                  data-testid="admin-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search catalogue..."
                  className="h-10 pl-10 bg-white border-amber-500/30 focus-visible:ring-amber-500/40"
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-amber-500/20">
                <Film className="h-10 w-10 text-amber-400 mx-auto" />
                <p className="mt-3 font-serif text-lg text-amber-900">No invites yet</p>
                <p className="text-stone-500 text-sm">Add your first video using the form.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((v) => (
                  <div
                    key={v.id}
                    data-testid={`admin-video-row-${v.id}`}
                    className="flex items-center gap-4 bg-white rounded-xl border border-amber-500/25 p-3 sm:p-4 hover:border-amber-500/50 transition-colors"
                  >
                    <img
                      src={v.thumbnail}
                      alt={v.title}
                      className="h-16 w-28 object-cover rounded-lg shrink-0 bg-stone-200"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300/60">
                          {v.category}
                        </span>
                        {v.featured && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-amber-400 to-amber-600 text-white">
                            <Star className="h-2.5 w-2.5 fill-current" /> Featured
                          </span>
                        )}
                      </div>
                      {inlineId === v.id ? (
                        <div className="mt-1 flex items-center gap-1.5">
                          <Input
                            data-testid={`inline-title-input-${v.id}`}
                            value={inlineTitle}
                            autoFocus
                            onChange={(e) => setInlineTitle(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveInline(); if (e.key === "Escape") cancelInline(); }}
                            className="h-8 bg-white border-amber-500/40 focus-visible:ring-amber-500/40"
                          />
                          <button
                            data-testid={`inline-save-${v.id}`}
                            onClick={saveInline}
                            disabled={inlineBusy}
                            className="p-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 shrink-0"
                          >
                            {inlineBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          </button>
                          <button
                            data-testid={`inline-cancel-${v.id}`}
                            onClick={cancelInline}
                            className="p-1.5 rounded-md bg-stone-200 text-stone-600 hover:bg-stone-300 shrink-0"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <h3
                          data-testid={`admin-video-title-${v.id}`}
                          onDoubleClick={() => startInline(v)}
                          title="Double-click to rename"
                          className="mt-1 font-medium text-amber-950 truncate cursor-text"
                        >
                          {v.title}
                        </h3>
                      )}
                    </div>
                    <Button
                      data-testid={`rename-video-btn-${v.id}`}
                      onClick={() => startInline(v)}
                      variant="ghost"
                      size="icon"
                      className="text-stone-400 hover:text-emerald-700 hover:bg-emerald-50 shrink-0"
                      title="Quick rename"
                    >
                      <Type className="h-5 w-5" />
                    </Button>
                    <Button
                      data-testid={`feature-video-btn-${v.id}`}
                      onClick={() => toggleFeatured(v)}
                      variant="ghost"
                      size="icon"
                      className={`shrink-0 ${v.featured ? "text-amber-500 hover:text-amber-600" : "text-stone-400 hover:text-amber-500"} hover:bg-amber-50`}
                      title={v.featured ? "Remove from Featured" : "Add to Featured"}
                    >
                      <Star className={`h-5 w-5 ${v.featured ? "fill-current" : ""}`} />
                    </Button>
                    <Button
                      data-testid={`edit-video-btn-${v.id}`}
                      onClick={() => openEdit(v)}
                      variant="ghost"
                      size="icon"
                      className="text-stone-400 hover:text-amber-700 hover:bg-amber-50 shrink-0"
                      title="Edit invite"
                    >
                      <Pencil className="h-5 w-5" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          data-testid={`delete-video-btn-${v.id}`}
                          variant="ghost"
                          size="icon"
                          className="text-stone-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this invite?</AlertDialogTitle>
                          <AlertDialogDescription>
                            “{v.title}” will be permanently removed from the catalogue. This cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            data-testid={`confirm-delete-btn-${v.id}`}
                            onClick={() => deleteVideo(v.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent data-testid="edit-video-dialog" className="bg-[#FAF6F0]">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-amber-950">Edit Invite</DialogTitle>
              <DialogDescription>Update the title, category, Vimeo link or price for this invite.</DialogDescription>
            </DialogHeader>
            {edit && (
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label className="text-amber-900 font-medium">Invite Title</Label>
                  <Input
                    data-testid="edit-title-input"
                    value={edit.title}
                    onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                    className="h-11 bg-white border-amber-500/30 focus-visible:ring-amber-500/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-amber-900 font-medium">Event Category</Label>
                  <Select value={edit.category} onValueChange={(val) => setEdit({ ...edit, category: val })}>
                    <SelectTrigger data-testid="edit-category-select" className="h-11 bg-white border-amber-500/30">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`edit-category-option-${c}`}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-amber-900 font-medium">Vimeo Link</Label>
                  <div className="relative">
                    <Link2 className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600" />
                    <Input
                      data-testid="edit-url-input"
                      value={edit.url}
                      onChange={(e) => setEdit({ ...edit, url: e.target.value })}
                      className="h-11 pl-10 bg-white border-amber-500/30 focus-visible:ring-amber-500/40"
                    />
                  </div>
                  <p className="text-xs text-stone-500">Change only if you want a different Vimeo video.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-amber-900 font-medium">Price <span className="font-normal text-stone-400">(blank = default {currency}{priceInput})</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-700 font-medium">{currency}</span>
                    <Input
                      data-testid="edit-price-input"
                      type="number"
                      value={edit.price}
                      onChange={(e) => setEdit({ ...edit, price: e.target.value })}
                      placeholder={`${priceInput} (default)`}
                      className="h-11 pl-7 bg-white border-amber-500/30 focus-visible:ring-amber-500/40"
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" data-testid="edit-cancel-btn" onClick={() => setEdit(null)} className="border-amber-500/40">
                Cancel
              </Button>
              <Button
                data-testid="edit-save-btn"
                onClick={saveEdit}
                disabled={editSaving}
                className="bg-amber-950 hover:bg-amber-900 text-amber-50"
              >
                {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={acctOpen} onOpenChange={setAcctOpen}>
          <DialogContent data-testid="account-dialog" className="bg-[#FAF6F0]">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-amber-950">Account Settings</DialogTitle>
              <DialogDescription>Change your admin login email or password.</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-stone-500 -mt-2">Signed in as <span className="font-medium text-amber-900">{user?.email}</span></p>

            <div className="mt-2 rounded-xl border border-amber-500/25 bg-white p-4">
              <h4 className="font-medium text-amber-950 flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-600" /> Change Password</h4>
              <div className="mt-3 space-y-3">
                <Input
                  data-testid="cp-current-input"
                  type="password"
                  value={cpCur}
                  onChange={(e) => setCpCur(e.target.value)}
                  placeholder="Current password"
                  className="h-11 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
                />
                <Input
                  data-testid="cp-new-input"
                  type="password"
                  value={cpNew}
                  onChange={(e) => setCpNew(e.target.value)}
                  placeholder="New password (min 6 characters)"
                  className="h-11 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
                />
                <Button
                  data-testid="cp-save-btn"
                  onClick={submitPassword}
                  disabled={cpBusy}
                  className="w-full h-11 bg-amber-950 hover:bg-amber-900 text-amber-50"
                >
                  {cpBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
                </Button>
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-amber-500/25 bg-white p-4">
              <h4 className="font-medium text-amber-950 flex items-center gap-2"><Mail className="h-4 w-4 text-amber-600" /> Change Email</h4>
              <div className="mt-3 space-y-3">
                <Input
                  data-testid="em-new-input"
                  type="email"
                  value={emNew}
                  onChange={(e) => setEmNew(e.target.value)}
                  placeholder="New email address"
                  className="h-11 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
                />
                <Input
                  data-testid="em-password-input"
                  type="password"
                  value={emPwd}
                  onChange={(e) => setEmPwd(e.target.value)}
                  placeholder="Confirm current password"
                  className="h-11 bg-[#FAF6F0] border-amber-500/30 focus-visible:ring-amber-500/40"
                />
                <Button
                  data-testid="em-save-btn"
                  onClick={submitEmail}
                  disabled={emBusy}
                  className="w-full h-11 bg-amber-950 hover:bg-amber-900 text-amber-50"
                >
                  {emBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Email"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={catOpen} onOpenChange={setCatOpen}>
          <DialogContent data-testid="categories-dialog" className="bg-[#FAF6F0]">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-amber-950">Manage Categories</DialogTitle>
              <DialogDescription>Add new event types or remove ones you don't use. A category can't be deleted while invites still use it.</DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-2">
              <Input
                data-testid="new-category-input"
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }}
                placeholder="New category name (e.g. Retirement)"
                className="h-11 bg-white border-amber-500/30 focus-visible:ring-amber-500/40"
              />
              <Button
                data-testid="add-category-btn"
                onClick={addCategory}
                disabled={catBusy}
                className="h-11 bg-amber-950 hover:bg-amber-900 text-amber-50 shrink-0"
              >
                {catBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add</>}
              </Button>
            </div>

            <div className="mt-2 max-h-72 overflow-auto space-y-2 pr-1">
              {categories.map((c) => (
                <div
                  key={c}
                  data-testid={`category-row-${c}`}
                  className="flex items-center justify-between gap-2 bg-white border border-amber-500/25 rounded-lg px-3 py-2"
                >
                  <span className="text-sm font-medium text-amber-950">{c}</span>
                  <button
                    data-testid={`delete-category-btn-${c}`}
                    onClick={() => removeCategory(c)}
                    className="p-1.5 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50"
                    title="Delete category"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
